import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Upload } from 'lucide-react';

interface Event {
  id: string;
  title: string;
  location: string;
  date: string;
  time: string;
  description: string;
  max_attendees: number;
  location_image?: string;
}

interface EditEventDialogProps {
  event: Event;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEventUpdated: () => void;
}

const EditEventDialog = ({ event, open, onOpenChange, onEventUpdated }: EditEventDialogProps) => {
  const [editEvent, setEditEvent] = useState(event);
  const { toast } = useToast();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Upload directly to the existing event-images bucket
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      
      const { data, error: uploadError } = await supabase.storage
        .from('event-images')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Error uploading image:', uploadError);
        throw uploadError;
      }

      // Get the public URL of the uploaded image
      const { data: urlData } = supabase.storage
        .from('event-images')
        .getPublicUrl(fileName);

      setEditEvent({...editEvent, location_image: urlData.publicUrl});
      toast({
        title: "Image Uploaded",
        description: "Location image has been uploaded successfully."
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: `Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    }
  };

  const updateEvent = async () => {
    try {
      if (!editEvent.title || !editEvent.location || !editEvent.date) {
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from('events')
        .update({
          title: editEvent.title,
          location: editEvent.location,
          date: editEvent.date,
          time: editEvent.time,
          description: editEvent.description,
          max_attendees: editEvent.max_attendees,
          location_image: editEvent.location_image
        })
        .eq('id', event.id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Event updated successfully"
      });
      
      onOpenChange(false);
      onEventUpdated();
    } catch (error) {
      console.error('Error updating event:', error);
      toast({
        title: "Error",
        description: "Failed to update event",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-title">Event Title *</Label>
            <Input
              id="edit-title"
              value={editEvent.title}
              onChange={(e) => setEditEvent({...editEvent, title: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="edit-location">Location *</Label>
            <Input
              id="edit-location"
              value={editEvent.location}
              onChange={(e) => setEditEvent({...editEvent, location: e.target.value})}
            />
          </div>
          <div>
            <Label htmlFor="edit-location-image">Location Picture</Label>
            <div className="flex items-center gap-2">
              <Input
                id="edit-location-image"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('edit-location-image')?.click()}
                className="flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Image
              </Button>
              {editEvent.location_image && (
                <span className="text-sm text-green-600">Image selected</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-date">Date *</Label>
              <Input
                id="edit-date"
                type="date"
                value={editEvent.date}
                onChange={(e) => setEditEvent({...editEvent, date: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-time">Time</Label>
              <Input
                id="edit-time"
                type="time"
                value={editEvent.time}
                onChange={(e) => setEditEvent({...editEvent, time: e.target.value})}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={editEvent.description}
              onChange={(e) => setEditEvent({...editEvent, description: e.target.value})}
              rows={3}
            />
          </div>
          <div>
            <Label htmlFor="edit-max-attendees">Max Attendees</Label>
            <Input
              id="edit-max-attendees"
              type="number"
              value={editEvent.max_attendees}
              onChange={(e) => setEditEvent({...editEvent, max_attendees: parseInt(e.target.value) || 100})}
            />
          </div>
          <Button onClick={updateEvent} className="w-full">
            Update Event
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditEventDialog;